'use client';

import type { ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { FindingsRenderer, parseFindingData } from './FindingsRenderer';
import type { Finding } from './types';
import type { OutputRenderContext } from '../../tool-config-types';
import type { OnOpenChatObject } from '../../hitl-types';
import { CheckmarkCircle01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

const readStringField = (
  data: Record<string, unknown>,
  keys: readonly string[],
): string | null => {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return null;
};

const extractFindingId = (content: unknown): string | null => {
  if (typeof content === 'string') {
    const match = content.match(
      /(?:finding\s*(?:created|updated)?:?\s*)?([a-f0-9]{24})/i,
    );

    return match ? match[1] : null;
  }

  if (typeof content === 'object' && content !== null) {
    const obj = content as Record<string, unknown>;
    const directId = readStringField(obj, [
      'findingId',
      'finding_id',
      'findingObjectId',
      'id',
      '_id',
    ]);

    if (directId) return directId;
    if (obj.finding && typeof obj.finding === 'object') {
      const nestedId = extractFindingId(obj.finding);

      if (nestedId) return nestedId;
    }

    if (typeof obj.message === 'string') {
      return extractFindingId(obj.message);
    }

    if (typeof obj.result === 'string') {
      return extractFindingId(obj.result);
    }

    if (typeof obj.output === 'string') {
      return extractFindingId(obj.output);
    }
  }

  return null;
};

function SuccessHeader({
  findingId,
  onOpenObject,
}: {
  findingId?: string | null;
  onOpenObject?: OnOpenChatObject;
}) {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const canOpenFinding = Boolean(findingId && onOpenObject);
  const handleOpenFinding = () => {
    if (!findingId || !onOpenObject) return;
    onOpenObject({
      type: 'finding',
      id: findingId,
      label: t('findingReviewFallbackTitle'),
    });
  };

  return (
    <HStack
      gap={1.5}
      alignItems="center"
      py={1}
      cursor={canOpenFinding ? 'pointer' : 'default'}
      onClick={canOpenFinding ? handleOpenFinding : undefined}
    >
      <Box color="green.9" display="flex" alignItems="center">
        <CheckmarkCircle01Icon size={13} />
      </Box>
      <Text fontSize="xs" color="fg.muted">
        {t('findingReviewSavedStatus')}
      </Text>
    </HStack>
  );
}

export const renderFindingResult = (ctx: OutputRenderContext): ReactNode => {
  const { content, maxHeight, input, onOpenObject } = ctx;
  const findingId = extractFindingId(content);

  if (findingId) {
    return <SuccessHeader findingId={findingId} onOpenObject={onOpenObject} />;
  }

  if (input && typeof input === 'object') {
    const findingFromInput = parseFindingData(input as Partial<Finding>);

    if (findingFromInput) {
      return (
        <VStack alignItems="start" gap={2} width="100%">
          <FindingsRenderer
            findings={[findingFromInput]}
            maxHeight={maxHeight}
            variant="compact"
            onOpenObject={onOpenObject}
          />
        </VStack>
      );
    }
  }

  if (typeof content === 'object' && content !== null) {
    const finding = parseFindingData(content as Partial<Finding>);

    if (finding) {
      return (
        <VStack alignItems="start" gap={2} width="100%">
          <SuccessHeader
            findingId={finding.findingId}
            onOpenObject={onOpenObject}
          />
          <FindingsRenderer
            findings={[finding]}
            maxHeight={maxHeight}
            variant="compact"
            onOpenObject={onOpenObject}
          />
        </VStack>
      );
    }
  }

  return null;
};
